'use client';

import { useMutation } from '@apollo/client/react';
import { isApolloError, getErrorCauseMessage } from '@/src/utils/error-handler';
import { PortalModal, usePortalContext } from '@cloai/uikit';

import { useTranslations } from 'next-intl';
import Image from 'next/image';
import { FC, useState } from 'react';
import { toast } from 'react-toastify';
import { twMerge } from 'tailwind-merge';

import { PORTALS } from '@/.constants/portals';
import { SEND_KEYBOARD, GENERATE_PHOTO_SESSION } from '@/.graphql';
import { Header } from '@/src/components/Header';
import {
  useStyleContext,
  useGTMContext,
  useProfileContext,
} from '@/src/providers';
import { miniApp } from '@tma.js/sdk-react';

const styles = {
  portal: '!h-dvh !max-h-dvh !rounded-none !bg-none !bg-fill-menu !p-0',
  head: 'p-5 absolute w-full top-0 left-0 z-10',
  wrapper: 'shrink-0 h-full',
  imageWrap:
    'relative shrink-0 flex flex-col items-center pb-6 mb-5 justify-end w-full h-[360px]',
  image: 'absolute w-full h-full top-0 left-0 object-cover object-top',
  gradient: 'absolute top-0 left-0 right-0 bottom-0 bg-gradient-sm',
  button: 'fixed bottom-10 left-5 right-5 bg-fill-button rounded-xl py-5 mt-4',
  buttonDisabled: 'bg-fill-toast',
  buttonText: 'text-label-black font-extrabold text-base',
  buttonTextDisabled: 'text-label-secondary',
  wrap: 'flex flex-col gap-4 p-4 mb-4 bg-fill-quinary rounded-xl',
};

export const ExampleImagePortal: FC = () => {
  const m = useTranslations('imageModal');
  const { closePortal, openPortal } = usePortalContext();
  const {
    activeStyle,
    genderMode,
    activeAvatar,
    filteredAvatars,
    activeAspectRatio,
    activeExample,
    isAvatarExist,
  } = useStyleContext();
  const profile = useProfileContext()!;
  const sendGTM = useGTMContext();
  const [buttonDisabled, setButtonDisabled] = useState(false);
  const [photosessionGeneration, { loading }] = useMutation(
    GENERATE_PHOTO_SESSION,
  );
  const [sendKeyboard] = useMutation(SEND_KEYBOARD);

  const handleClosePortal = () => {
    closePortal();

    openPortal(PORTALS.GenerateImage);
  };

  const handleClickGenerate = async () => {
    const signs =
      [activeStyle?.isPopular && 'popular', activeStyle?.isNew && 'new']
        .filter(Boolean)
        .join(', ') || 'none';

    sendGTM({
      event: 'example_fotosession_generation',
      category: null,
      name: activeStyle?.name['en'],
      gender: genderMode,
      signs,
    });

    try {
      const { data } = await photosessionGeneration({
        variables: {
          photoSessionId: activeStyle?.id,
          loraId: activeAvatar,
          resolution: activeAspectRatio,
        },
      });
      if (!data) {
        return;
      }
      const { queueTime } = data.generatePhotoSession;

      toast(m('successMessage', { queueTime }), {
        position: 'bottom-center',
        icon: (
          <Image
            width={16}
            height={18}
            src="/icons/check.svg"
            alt={activeStyle.name[profile?.lang]}
            className="w-4 h-4"
          />
        ),
        theme: 'dark',
        style: {
          backgroundColor: '#3D3E41',
          color: '#FFFFFF',
          fontSize: '14px',
          fontWeight: '500',
          borderRadius: '12px',
          padding: '16px',
          bottom: '32px',
          maxWidth: 'calc(100% - 40px)',
          alignItems: 'start',
        },
      });

      setButtonDisabled(true);
      setTimeout(() => setButtonDisabled(false), 5000);
    } catch (err) {
      const error = err as Error;
      if (isApolloError(error)) {
        const causeMessage = getErrorCauseMessage(error);
        if (causeMessage === 'not_enough_image_generations') {
          closePortal();
          openPortal(PORTALS.TopUpBalance);
        } else {
          toast.error(causeMessage || error.message);
        }
      }
    }
  };

  const onCloseWebApp = async () => {
    try {
      await sendKeyboard({
        variables: {
          keyboard: 'createAvatar',
        },
      });
      if (miniApp.close.isAvailable()) {
        miniApp.close();
      }
    } catch (error) {
      console.error(error);
    }
  };

  if (!activeStyle) {
    return null;
  }

  return (
    <PortalModal
      portalId={PORTALS.ExampleImage}
      className={styles.portal}
      noClose
    >
      <div className={styles.head}>
        <Header isCloseButton onClickCloseButton={handleClosePortal} />
      </div>

      <div className={styles.wrapper}>
        <div className={styles.imageWrap}>
          <Image
            fill
            src={activeExample}
            alt={activeStyle.name[profile?.lang]}
            className={styles.image}
          />
          <div className={styles.gradient} />
        </div>

        {filteredAvatars.length ? (
          <button
            disabled={buttonDisabled || loading || !isAvatarExist}
            onClick={handleClickGenerate}
            className={twMerge(
              styles.button,
              (buttonDisabled || loading || !isAvatarExist) &&
                styles.buttonDisabled,
            )}
          >
            <div
              className={twMerge(
                styles.buttonText,
                (buttonDisabled || loading || !isAvatarExist) &&
                  styles.buttonTextDisabled,
              )}
            >
              {m('buttonPhotoshoot')}
            </div>
          </button>
        ) : (
          <button className={styles.button} onClick={onCloseWebApp}>
            <div className={styles.buttonText}>{m('buttonAvatar')}</div>
          </button>
        )}
      </div>
    </PortalModal>
  );
};
